import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { AccessErrorAlert } from "@/shared/organizations/AccessErrorAlert";
import { LocationPicker } from "@/shared/organizations/LocationPicker";
import { OrganizationPicker } from "@/shared/organizations/OrganizationPicker";
import type { AdminMembership } from "@/shared/organizations/membership-types";
import { AddOrganizationRoleFieldset } from "./AddOrganizationRoleFieldset";
import { useAddToOrganization, type AddToOrganizationOutcome } from "./use-add-to-organization";

export type { AddToOrganizationOutcome };

interface AddToOrganizationDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly userId: string;
  /** The user's memberships: tells "add" apart from "assign more locations". */
  readonly memberships: readonly AdminMembership[];
  /**
   * The viewer holds `org.admins.manage`: only then is ADMIN offered. Without
   * it the API would refuse the grant, so the option is not shown at all.
   */
  readonly canGrantAdmin: boolean;
  /** Fired after the API accepted the write, right before the dialog closes. */
  readonly onDone: (outcome: AddToOrganizationOutcome) => void;
}

/**
 * Admin "add to organization" flow. Any non-INTERNAL account can join any
 * organization as ADMIN or STAFF, whatever its legacy label: pick the
 * organization (name + kind), the role and — for STAFF — the locations of
 * THAT organization. The user is added directly, without an invitation.
 *
 * If the user is already STAFF there, the same dialog assigns more locations
 * (the organization's assignments route); an ADMIN / OWNER there is told it is already
 * a member. The API re-validates everything (eligibility, partition, scope).
 */
export function AddToOrganizationDialog(props: AddToOrganizationDialogProps) {
  // Mounted only while open: the form always starts clean.
  if (!props.open) return null;
  return <AddToOrganizationDialogBody {...props} />;
}

function AddToOrganizationDialogBody({
  onOpenChange,
  userId,
  memberships,
  canGrantAdmin,
  onDone,
}: AddToOrganizationDialogProps) {
  const { t } = useTranslation();
  const form = useAddToOrganization({ userId, memberships, canGrantAdmin, onDone, onOpenChange });
  const { organization, blocked, role, assigningMore, isPending, access } = form;

  return (
    <Dialog open onOpenChange={access.handleOpenChange}>
      <DialogContent data-testid="add-org-dialog" className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("organizationAccess.add.title")}</DialogTitle>
          <DialogDescription>{t("organizationAccess.add.description")}</DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-4">
          <div className="space-y-2">
            <Label>{t("organizationAccess.add.organizationLabel")}</Label>
            <OrganizationPicker
              selected={organization}
              onSelect={form.chooseOrganization}
              onClear={() => form.chooseOrganization(null)}
              disabled={isPending}
            />
          </div>

          {organization && (form.alreadyMember || form.pendingConflict) && (
            <p data-testid="add-org-already-member" className="text-sm text-destructive">
              {form.alreadyRole
                ? t(`organizationAccess.errors.alreadyMemberRole.${form.alreadyRole}`)
                : t("organizationAccess.errors.alreadyMember")}
            </p>
          )}

          {organization && form.inactiveMembership && (
            <p data-testid="add-org-inactive-member" className="text-sm text-destructive">
              {t("organizationAccess.errors.inactiveMembership")}
            </p>
          )}

          {organization && !blocked && (
            <AddOrganizationRoleFieldset
              options={form.roleOptions}
              value={role}
              onChange={form.chooseRole}
              disabled={isPending || assigningMore}
            />
          )}

          {organization && !blocked && role === "STAFF" && (
            <div className="space-y-2">
              <Label>{t("organizationAccess.add.locationsLabel")}</Label>
              <LocationPicker
                organizationId={organization.id}
                selected={form.selected}
                onToggle={form.toggle}
                disabledIds={form.activeLocationIds}
                disabled={isPending}
                testIdPrefix="add-org"
              />
            </div>
          )}

          {organization && !blocked && !assigningMore && (
            <p data-testid="add-org-notify-note" className="text-xs text-muted-foreground">
              {t("organizationAccess.add.notifyNote")}
            </p>
          )}

          {access.error && <AccessErrorAlert testId="add-org-error" message={access.error} />}
        </div>

        <DialogFooter>
          <Button
            data-testid="add-org-cancel"
            variant="outline"
            onClick={() => access.handleOpenChange(false)}
            disabled={isPending}
          >
            {t("organizationAccess.cancel")}
          </Button>
          <Button
            data-testid="add-org-submit"
            onClick={() => void access.submit()}
            disabled={!form.canSubmit}
          >
            {isPending ? t("organizationAccess.add.pending") : t("organizationAccess.add.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
